import {
    PerformanceBanner,
    PerformanceManagementBase,
    PerformanceSubtask,
    PerformanceSubtaskDialog,
    PerformanceTask,
} from '@strata/react-performance';
import { PerformanceStatusIndicator } from '@strata/react-status-indicators';
import { Worklist, WorklistTask } from '@strata/react-worklist';
import { useEffect, useState } from 'react';
import { Location, useLocation, useNavigationType } from 'react-router-dom';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import {
    downloadPerformanceFile,
    removePerformanceFile,
    uploadPerformanceFiles,
} from 'services/Files';
import { showDialogForWorklistTypes, useReturnToWorklistCheck } from 'services/worklistHelpers';
import { useAppDispatch, useAppSelector } from 'store';
import { submitPerformanceConditions } from 'store/wonReferrals/performanceThunks';

const getElement = ({
    distinctId,
    namespace,
    root,
}: {
    distinctId: string;
    namespace: string;
    root?: PerformanceTask[];
}) => {
    const idKey = `${namespace}_id`;
    const elementsFound: [PerformanceSubtask, string][] = [];
    const findElements = (object?: PerformanceTask[] | PerformanceTask, path = '') => {
        const objectEntries = Object.entries(object ?? {});
        objectEntries.forEach(([key, value]) => {
            if ((key === 'distinctId' || key === idKey) && value === distinctId) {
                elementsFound.push([
                    structuredClone(object as PerformanceSubtask),
                    `${path}.${key}`,
                ]);
            } else if (typeof value === 'object' && value != null) {
                if (Array.isArray(value)) {
                    (value as PerformanceSubtask[]).forEach((element, index) => {
                        findElements(element, `${path}.${index}`);
                    });
                } else {
                    findElements(value as PerformanceTask, `${path}.${key}`);
                }
            }
        });
    };
    findElements(root);
    elementsFound.sort(([, aPath], [, bPath]) => {
        const aLength = aPath.split('.').length;
        const bLength = bPath.split('.').length;
        return aLength - bLength;
    });
    return elementsFound[0];
};

interface PerformanceComponentProps {
    performanceRequirement: {
        referral_id: string;
        value_case_id: string;
        value_case_name: string;
        value_case_description: string;
        performance_start_date?: string;
        performance_end_date?: string;
        evaluation_end_date?: string;
        status?: (typeof PerformanceStatusIndicator.Statuses)[keyof typeof PerformanceStatusIndicator.Statuses];
        incentive: number;
        banners?: PerformanceBanner[];
        tasks?: PerformanceTask[];
    };
}

export const PerformanceComponent = ({ performanceRequirement }: PerformanceComponentProps) => {
    const dispatch = useAppDispatch();
    const navigationType = useNavigationType();
    const location = useLocation() as Location<{ worklistTask?: WorklistTask }>;
    const writePerformance = usePermissionCheck(Permissions.WRITE_PERFORMANCE);
    const returnToWorklistCheck = useReturnToWorklistCheck();
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);

    const [subtaskDialogToShow, setSubtaskDialogToShow] = useState(
        // Show subtask dialog from worklist task if present
        showDialogForWorklistTypes({
            types: [Worklist.WorkTaskType.MANSUBTASK],
            navigationType,
            location,
        })
            ? location.state?.worklistTask?.linking_id
            : undefined,
    );
    const [subtaskObject, setSubtaskObject] = useState<PerformanceSubtask>();

    useEffect(() => {
        if (subtaskDialogToShow) {
            const subtask = getElement({
                distinctId: subtaskDialogToShow,
                namespace: 'subtask',
                root: performanceRequirement.tasks,
            })?.[0];

            if (subtask) {
                // Disable dialog inputs if user doesn't have write:performance permission
                if (!writePerformance) {
                    subtask.disableDialog = true;
                }
                setSubtaskObject(subtask);
            }
        } else {
            setSubtaskObject(undefined);
        }
    }, [performanceRequirement, subtaskDialogToShow, writePerformance]);

    return (
        <>
            <PerformanceSubtaskDialog
                isOpen={!!subtaskObject}
                isCloseButtonShown
                subtaskObject={subtaskObject}
                onSubmit={(subtaskValues) =>
                    dispatch(
                        submitPerformanceConditions(
                            performanceRequirement?.referral_id,
                            subtaskValues.conditions,
                            () => {
                                setSubtaskDialogToShow(undefined);
                                returnToWorklistCheck();
                            },
                        ),
                    )
                }
                onCancel={() => setSubtaskDialogToShow(undefined)}
                fileUploadConfig={{
                    onAddFiles: (files: File[], conditionId: string) =>
                        uploadPerformanceFiles({
                            npi: selectedNpi!,
                            files,
                            referralId: performanceRequirement?.referral_id,
                            conditionId,
                        }),
                    onRemoveFile: (filename: string, conditionId: string) =>
                        removePerformanceFile({
                            npi: selectedNpi!,
                            filename,
                            referralId: performanceRequirement?.referral_id,
                            conditionId,
                        }),
                    onFileClick: (filename: string, conditionId: string) =>
                        downloadPerformanceFile({
                            npi: selectedNpi!,
                            filename,
                            referralId: performanceRequirement?.referral_id,
                            conditionId,
                        }),
                }}
                usePortal
            />
            <PerformanceManagementBase
                performanceRequirementId={performanceRequirement?.value_case_id}
                performanceStatus={performanceRequirement?.status}
                incentiveAmount={performanceRequirement?.incentive}
                performanceName={performanceRequirement?.value_case_name}
                performanceDescription={performanceRequirement?.value_case_description}
                performanceDueDate={performanceRequirement?.performance_end_date}
                performanceEvaluationDate={performanceRequirement?.evaluation_end_date}
                onAddDocumentationClick={(subtaskId) => setSubtaskDialogToShow(subtaskId)}
                onViewClaimClick={(subtaskId) => setSubtaskDialogToShow(subtaskId)}
                tasksData={performanceRequirement?.tasks}
                banners={performanceRequirement?.banners}
            />
        </>
    );
};
